import Alpine from 'alpinejs';

type CarouselConfig = {
    /** `Slide %d`. */
    stopLabelFormat: string;
    /** `%1$d av %2$d`. */
    slideLabelFormat: string;
};

const TOLERANCE = 2;

Alpine.data(
    'app_view_components_blocks_Carousel',
    ({ stopLabelFormat, slideLabelFormat }: CarouselConfig) => ({
        stopLabelFormat,
        slideLabelFormat,

        slides: [] as Element[],
        /** Possible stops */
        stopOffsets: [0] as number[],
        /** Nearest stop to where we are now. */
        stopIndex: 0,
        /** Stop an in-flight programmatic scroll is aimed at. */
        pendingIndex: null as number | null,

        resizeObserver: null as ResizeObserver | null,

        init() {
            this.$nextTick(() => {
                this.slides = Array.from(this.$refs.scroller.children);

                this.resizeObserver = new ResizeObserver(() => this.measure());
                this.resizeObserver.observe(this.$refs.scroller);

                this.nameSlides();
                this.measure();
            });
        },

        destroy() {
            this.resizeObserver?.disconnect();
        },

        nameSlides() {
            const slides = this.slides;
            const numSlides = slides.length;

            slides.forEach((slide, i) => {
                const label = this.slideLabelFormat
                    .replace('%1$d', String(i + 1))
                    .replace('%2$d', String(numSlides));

                slide.setAttribute('aria-label', label);
            });
        },

        measure() {
            const scroller = this.$refs.scroller;

            const originLeft =
                scroller.getBoundingClientRect().left - scroller.scrollLeft;
            const maxScroll = Math.max(
                0,
                scroller.scrollWidth - scroller.clientWidth,
            );

            this.stopOffsets = this.slides
                .map((slide) => slide.getBoundingClientRect().left - originLeft)
                .map((offset) => Math.min(offset, maxScroll))
                .filter(
                    (offset, i, all) =>
                        i === 0 || offset > all[i - 1] + TOLERANCE,
                );

            this.pendingIndex = null;

            this.updateStopIndex();
        },

        nearestStopIndex() {
            const { scrollLeft } = this.$refs.scroller;

            return this.stopOffsets.reduce(
                (nearest, offset, i) =>
                    Math.abs(offset - scrollLeft) <
                    Math.abs(this.stopOffsets[nearest] - scrollLeft)
                        ? i
                        : nearest,
                0,
            );
        },

        isPassingThrough(index: number) {
            if (this.pendingIndex !== null && this.pendingIndex !== index) {
                return true;
            }

            this.pendingIndex = null;

            return false;
        },

        updateStopIndex() {
            const nearest = this.nearestStopIndex();

            if (this.isPassingThrough(nearest)) {
                return;
            }

            this.stopIndex = nearest;
        },

        settleStopIndex() {
            this.pendingIndex = null;

            this.updateStopIndex();
        },

        goToStop(i: number) {
            const index = Math.min(Math.max(i, 0), this.stopOffsets.length - 1);

            this.stopIndex = index;
            this.pendingIndex = index;

            this.$refs.scroller.scrollTo({ left: this.stopOffsets[index] });
        },

        goToRelativeStop(delta: number) {
            this.goToStop(this.stopIndex + delta);
        },

        stopLabel(i: number) {
            return this.stopLabelFormat.replace('%d', String(i + 1));
        },

        get isScrollable() {
            return this.stopOffsets.length > 1;
        },

        get canGoPrev() {
            return this.stopIndex > 0;
        },

        get canGoNext() {
            return this.stopIndex < this.stopOffsets.length - 1;
        },

        scroller: {
            'x-ref': 'scroller',
            ':tabindex': 'isScrollable ? 0 : null',
            '@scroll.passive': 'updateStopIndex',
            // However the scroll ended, the stop we settled on is now ours.
            '@scrollend.passive': 'settleStopIndex',
            // Scrolling by hand cancels our smooth scroll short of its target.
            '@pointerdown.passive': 'pendingIndex = null',
            '@wheel.passive': 'pendingIndex = null',
            '@keydown': 'pendingIndex = null',
        },

        prevArrow: {
            'x-show': 'isScrollable',
            ':aria-disabled': 'canGoPrev ? "false" : "true"',
            '@click': 'goToRelativeStop(-1)',
        },

        nextArrow: {
            'x-show': 'isScrollable',
            ':aria-disabled': 'canGoNext ? "false" : "true"',
            '@click': 'goToRelativeStop(1)',
        },

        indicators: {
            'x-show': 'isScrollable',
        },

        indicatorTemplate: {
            'x-for': '(offset, i) in stopOffsets',
            ':key': 'i',
        },

        indicator: {
            ':class': 'stopIndex === i ? "opacity-100" : "opacity-25"',
            ':aria-current': 'stopIndex === i ? "true" : "false"',
            ':aria-label': 'stopLabel(i)',
            '@click': 'goToStop(i)',
        },
    }),
);
